
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { SupplierModel } from '@/models/supplier';
import { PurchaseOrderModel } from '@/models/purchase_order';
import { ReportModel } from "@/models/report";
import { FilterColumnList } from "@/collections/filter_columns";
import { FilterColumnModel } from '@/models/filter_column';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import FeatureLockedNotify from '@/components/FeatureLockedNotify.vue';
import { StoreModel } from '@/models/store';
import DateFilter from '@/components/DateFilter.vue';
import { StoreLocationList } from '@/collections/store_locations';
import { StoreLocationModel } from '@/models/store_location';
import { FEATURES } from '@/config/featureConfig';
import ForecastingMethodSelector from '@/components/ForecastingMethodSelector.vue';
import LocationFilter from '@/components/LocationFilter.vue';
import { EventBus } from "@/main";


@Component({
  components: {
    FeatureLockedNotify,
    DateFilter,
    ForecastingMethodSelector,
    LocationFilter
  }
})
export default class PurchaseOrderCreateView extends Vue {
  private CurrencyHelper = CurrencyHelper;
  private supplier: SupplierModel | null = null;
  private purchaseOrder: PurchaseOrderModel = new PurchaseOrderModel({});
  private availableProducts: any[] = [];
  private selectedProducts: any[] = [];
  private search = '';
  private submitting = false;
  private filterType = 'all';
  private loadingAvailableProduct = false;
  private showDetails = true;
  private orderDateMenu = false;
  private expectedDeliveryDateMenu = false;
  private dates: string[] = [];
  private defaultRange = '30d';
  private showNeedReorderNowOnly = true;
  private isCreated: boolean = false;
  private activeLocationFilters: FilterColumnList | null = null;
  private loadingLocations = false;
  private locations: StoreLocationModel[] = [];
  private storeLocationList = new StoreLocationList();
  private productColumn:string = 'Item[Item Description]';

  // New properties for forecast settings - REMOVED as they are now in child component
  // private forecastSetting: ForecastSetting = new ForecastSetting();
  // private loadingForecastSettings: boolean = false;
  // private selectedForecastingMethod: string | null = null;

  private dataTableOptions: any = {
    page: 1,
    itemsPerPage: 25,
    sortBy: ['[inventory_replenishment]'],
    sortDesc: [true],
    groupBy: [],
    groupDesc: [],
    multiSort: false,
    mustSort: false
  };
  private totalAvailableProducts = 0;

  private headers = [
    { text: 'Image', value: 'variant_image', sortable: false, width: '80px' },
    { text: 'Product', value: this.productColumn},
    { text: 'Historical Sales', value: 'historicalSales' },
    { text: 'Reorder Date', value: 'reorderDate' },
    { text: 'Suggested Qty', value: '[inventory_replenishment]', align: 'center' },
    { text: 'Quantity', value: 'quantity', width: '120px' },
    { 
      text: 'Unit Cost', 
      value: '[average_unit_cost]', 
      width: '120px'
    },
    { 
      text: 'Total', 
      value: 'total', 
      width: '120px',
      format: (value: number) => CurrencyHelper.formatCurrency(value)
    }
  ];
  private requiredRule = (v: string) => !!v || 'Field is required';

  private rules = {
    quantity: [
      (v: number | string) => v !== '' && v !== null || 'Quantity is required',
      (v: number | string) => Number(v) > 0 || 'Quantity must be greater than 0'
    ],
    unitCost: [
       (v: number | string) => v !== '' && v !== null || 'Unit Cost is required',
       (v: number | string) => Number(v) >= 0 || 'Unit Cost must be non-negative'
    ]
  };

  @Prop({ default: () => new StoreModel() })
  private store!: StoreModel;

  private get canManagePurchaseOrders(): boolean {
    const validateValues = [
      {
        code: 'purchase_order_management',
        value: 'unlocked'
      }
    ];
    const validateResult = this.store.validateRule(validateValues);
    return validateResult.isValidate;
  }

  @Watch('dates')
  async onDatesChanged() {
    if (!this.isCreated) {
      return;
    }
    if (this.dataTableOptions.page !== 1) {
      this.dataTableOptions.page = 1;
    } else {
      await this.fetchData();
    }
  }

  @Watch('dataTableOptions')
  async onTableOptionsChanged(newValue: any, oldValue: any) {
    if (!this.isCreated) {
      return;
    }
    // Only fetch data if page or itemsPerPage changes. Sorting/filtering handled by other watchers or events.
    if (newValue.page !== oldValue.page || newValue.itemsPerPage !== oldValue.itemsPerPage ||
        JSON.stringify(newValue.sortBy) !== JSON.stringify(oldValue.sortBy) ||
        JSON.stringify(newValue.sortDesc) !== JSON.stringify(oldValue.sortDesc)
        ) {
      await this.fetchData();
    }
  }

  async created() {
    const supplierIdParam = this.$route.params.supplierId;
    if (supplierIdParam) {
      this.supplier = new SupplierModel({ id: Number(supplierIdParam) });
      await this.supplier.fetch();
    }
    this.initializePurchaseOrder();
    
    // Now fetch locations and product data
    await this.fetchLocations();
    if (this.supplier) {
      await this.fetchData();
    }
    this.isCreated = true;
  }

  private initializePurchaseOrder() {
    const baseOrderDateStr = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD

    this.purchaseOrder = new PurchaseOrderModel({
      poNumber: this.generatePoNumber(),
      orderDate: baseOrderDateStr,
      supplierId: this.supplier?.id,
      supplierName: this.supplier?.name,
      // expectedDeliveryDate will be set below
    });

    // Calculate and set expectedDeliveryDate based on supplier lead time
    if (this.supplier && typeof this.supplier.leadTime === 'number' && this.supplier.leadTime >= 0) {
        const orderDate = new Date(baseOrderDateStr);
        const leadTimeDays = this.supplier.leadTime;
        const expectedDate = new Date(orderDate); // Create a new date object
        expectedDate.setDate(orderDate.getDate() + leadTimeDays);
        this.purchaseOrder.expectedDeliveryDate = expectedDate.toISOString().substr(0, 10);
    } else {
        // Set to undefined if supplier or leadTime is invalid/missing
        this.purchaseOrder.expectedDeliveryDate = undefined;
    }
  }

  private generatePoNumber(): string {
    const prefix = 'PO';
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const randomNumber = Math.floor(Math.random() * 90000 + 10000); // 5-digit random number
    return `${prefix}${date}-${randomNumber}`;
  }

  private async fetchLocations() {
    this.loadingLocations = true;
    try {
      await this.storeLocationList.fetch();
      this.locations = this.storeLocationList.items;
    } catch (error) {
      this.$emit('error', 'Failed to load store locations');
    } finally {
      this.loadingLocations = false;
    }
  }

  private async fetchData() {
    this.loadingAvailableProduct = true;
    try {
      const supplierId = Number(this.$route.params.supplierId);
      if (!this.supplier || this.supplier.id !== supplierId) {
        this.supplier = new SupplierModel({ id: supplierId });
        await this.supplier.fetch();
          this.purchaseOrder = new PurchaseOrderModel({
          poNumber: this.generatePoNumber(),
          orderDate: new Date().toISOString().substr(0, 10),
          supplierId: supplierId,
        });
      }

      

      const filters = new FilterColumnList();
      if (this.search && this.search.trim() !== '') {
        const searchFilter = new FilterColumnModel();
        searchFilter.mapData({
          code: 'Item[Item Description]',
          name: 'Product Search',
          value: [this.search.trim()],
          operator: 'contains',
          dataType: 'string'
        });
        filters.add(searchFilter);
      }

      // Add filters from LocationFilter component
      if (this.activeLocationFilters && this.activeLocationFilters.items.length > 0) {
        this.activeLocationFilters.items.forEach(filter => {
          filters.add(filter);
        });
      }
      
      const pageOptions = {
        page: this.dataTableOptions.page,
        itemsPerPage: this.dataTableOptions.itemsPerPage === -1 ? 10000 : this.dataTableOptions.itemsPerPage,
        sortBy: this.dataTableOptions.sortBy,
        sortDesc: this.dataTableOptions.sortDesc
      };
      
      const reportOptions: any = {
        initialFilterColumns: filters,
        pageOptions: pageOptions,
        suggestFutureReorderQuantity: !this.showNeedReorderNowOnly
      };
      
      const countReportOptions: any = {
        initialFilterColumns: filters,
        suggestFutureReorderQuantity: !this.showNeedReorderNowOnly
      };
      
      if (this.dates && this.dates.length >= 2) {
        reportOptions.dateRange = {
          start: this.dates[0],
          end: this.dates[1]
        };
        countReportOptions.dateRange = {
          start: this.dates[0],
          end: this.dates[1]
        };
      }
      
      const [reportModel, countResponse]: [ReportModel, { totalItems: number } | any] = await Promise.all([
        this.supplier.fetchProductsWithOptions(
          'inventory_replenishment',
          'eq',
          reportOptions
        ),
        this.supplier.countProductsWithOptions(
          'inventory_replenishment',
          'eq',
          countReportOptions
        )
      ]);
      this.availableProducts = reportModel.getReportRowsData().map(p => {
        const initialQuantity = Math.round(Number(p['[inventory_replenishment]'])) || 0;
        const originalCost = Number(p['[average_unit_cost]']) || Number(p['[variant_unit_cost]']) || Number(p.unit_cost) || 0;
        
        this.$set(p, 'quantity', initialQuantity);
        this.$set(p, 'original_average_unit_cost', originalCost);
        this.$set(p, '[average_unit_cost]', originalCost);
        this.$set(p, 'total', initialQuantity * originalCost);
        this.$set(p, 'reorderDate', p['[reorder_date]']);
        return p;
      });
      this.totalAvailableProducts = countResponse.totalItems || 0;
    } catch (error) {
      this.$emit('error', 'Failed to load products for supplier');
    } finally {
      this.loadingAvailableProduct = false;
    }
  }

  private formatDate(value: any): string {
    if (!value) return '-';
    
    try {
      // If it's a timestamp (number)
      if (typeof value === 'number') {
        const date = new Date(value);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      
      // If it's a string, try to parse it
      if (typeof value === 'string') {
        const date = new Date(value);
        if (isNaN(date.getTime())) return '-'; // Invalid date
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      
      // If it's already a Date object
      if (value instanceof Date) {
        return value.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      
      return '-';
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  }

  private formatNumber(value: any, decimals: number = 2): string {
    if (value === null || value === undefined) return '0.00';
    if (typeof value === 'string') value = parseFloat(value);
    if (typeof value !== 'number' || isNaN(value)) return '0.00';
    return value.toFixed(decimals);
  }

  private formatCurrency(value: number): string {
    return CurrencyHelper.formatCurrency(value);
  }

  private getStockLevelColor(product: any): string {
    const stockLevel = product['[Actual Inventory Quantity]'] || product.currentInventory;
    const reorderQuantity = product['[inventory_replenishment]'] || product.reorderQuantity;
    
    // If we have less than half of the recommended reorder quantity, it's critical
    if (stockLevel <= (reorderQuantity * 0.5)) return 'error';
    
    // If we have less than the recommended reorder quantity, it's a warning
    if (stockLevel <= reorderQuantity) return 'warning';
    
    // Otherwise, we're good
    return 'success';
  }

  private updateItemTotal(item: any) {
    const quantity = Number(item.quantity) || 0;
    const editedCost = item['[average_unit_cost]'];
    const originalCost = item.original_average_unit_cost || 0;
    let effectiveCost = originalCost;

    if (editedCost !== null && editedCost !== undefined && editedCost !== '') {
      const numEditedCost = Number(editedCost);
      if (!isNaN(numEditedCost) && numEditedCost >= 0) {
        effectiveCost = numEditedCost;
      }
    }
    
    this.$set(item, 'total', quantity * effectiveCost);
  }

  get totalValue(): number {
    return this.selectedProducts.reduce((sum, item) => {
      const quantity = Number(item.quantity) || 0;
      const editedCost = item['[average_unit_cost]'];
      const originalCost = item.original_average_unit_cost || 0;
      let effectiveCost = originalCost;
      
      if (editedCost !== null && editedCost !== undefined && editedCost !== '') {
        const numEditedCost = Number(editedCost);
        if (!isNaN(numEditedCost) && numEditedCost >= 0) {
          effectiveCost = numEditedCost;
        }
      }

      return sum + (quantity * effectiveCost);
    }, 0);
  }

  get isValid(): boolean {
    if (!this.purchaseOrder.poNumber || 
        !this.purchaseOrder.orderDate ||
        !this.purchaseOrder.locationId ||
        !this.supplier ||
        this.selectedProducts.length === 0) {
      return false;
    }

    if (!this.isValidOrderValue) {
      return false;
    }

    return this.selectedProducts.every(item => {
      const quantity = Number(item.quantity);
      if (isNaN(quantity) || quantity <= 0) {
        return false; 
      }

      const editedCost = item['[average_unit_cost]'];
      const originalCost = item.original_average_unit_cost || 0;
      let effectiveCost = -1; 

      if (editedCost !== null && editedCost !== undefined && editedCost !== '') {
        const numEditedCost = Number(editedCost);
        if (!isNaN(numEditedCost) && numEditedCost >= 0) {
          effectiveCost = numEditedCost;
        } else {
          return false; 
        }
      } else {
          effectiveCost = originalCost;
      }
      
      if (effectiveCost < 0) {
          return false;
      }
      
      return true; 
    });
  }

  get isValidOrderValue(): boolean {
    if (!this.supplier) return false;
    const minOrder = Number(this.supplier.minOrderValue);
    const maxOrder = Number(this.supplier.maxOrderValue);
    if (this.totalValue < minOrder && minOrder > 0) return false;
    if (this.totalValue > maxOrder && maxOrder > 0) return false;
    return true;
  }

  get getOrderValueStatus(): string {
    if (!this.supplier) return 'grey';
    const minOrder = Number(this.supplier.minOrderValue);
    const maxOrder = Number(this.supplier.maxOrderValue);

    if (this.totalValue < minOrder && minOrder > 0) return 'error';
    if (this.totalValue > maxOrder && maxOrder > 0) return 'error';
    return 'success';
  }

  get getOrderValueMessage(): string {
    if (!this.supplier) return 'Invalid';
    const minOrder = Number(this.supplier.minOrderValue);
    const maxOrder = Number(this.supplier.maxOrderValue);

    if (this.totalValue < minOrder && minOrder > 0) {
      return `Below Minimum (${this.CurrencyHelper.formatCurrency(this.totalValue)} < ${this.CurrencyHelper.formatCurrency(minOrder)})`;
    }
    if (this.totalValue > maxOrder && maxOrder > 0) {
      return `Exceeds Maximum (${this.CurrencyHelper.formatCurrency(this.totalValue)} > ${this.CurrencyHelper.formatCurrency(maxOrder)})`;
    }
    return `Valid Order Value (${this.CurrencyHelper.formatCurrency(this.totalValue)})`;
  }

  get filteredProducts(): any[] {
    switch (this.filterType) {
      case 'selected':
        return this.availableProducts.filter(p => this.selectedProducts.some(s => s.variant_id === p.variant_id));
      case 'not-selected':
        return this.availableProducts.filter(p => !this.selectedProducts.some(s => s.variant_id === p.variant_id));
      default:
        return this.availableProducts;
    }
  }

  get locationOptions() {
    return [
      { id: null, name: 'Select Location' },
      ...this.locations.map((loc: StoreLocationModel) => ({ id: loc.id, name: loc.name }))
    ];
  }

  private async handleSearchTrigger() {
    if (this.dataTableOptions.page !== 1) {
      this.dataTableOptions.page = 1;
    } else {
      await this.fetchData();
    }
  }

  private async submitPurchaseOrder() {
    this.submitting = true;

    if (!this.isValid) {
      let errorMessages: string[] = [];
      if (!this.purchaseOrder.poNumber) {
        errorMessages.push('PO Number is required.');
      }
      if (!this.purchaseOrder.orderDate) {
        errorMessages.push('Order Date is required.');
      }
      if (!this.purchaseOrder.locationId) {
        errorMessages.push('Store Location is required.');
      }
      if (!this.supplier) {
        errorMessages.push('Supplier information is missing.');
      }
      if (this.selectedProducts.length === 0) {
        errorMessages.push('At least one product must be selected.');
      }

      this.selectedProducts.forEach(item => {
        const quantity = Number(item.quantity);
        if (isNaN(quantity) || quantity <= 0) {
          errorMessages.push(`Invalid quantity for product '${item['Item[Item Description]'] || item.variant_id}'. Quantity must be greater than 0.`);
        }

        const editedCost = item['[average_unit_cost]'];
        if (editedCost !== null && editedCost !== undefined && editedCost !== '') {
          const numEditedCost = Number(editedCost);
          if (isNaN(numEditedCost) || numEditedCost < 0) {
            errorMessages.push(`Invalid unit cost for product '${item['Item[Item Description]'] || item.variant_id}'. Cost must be a non-negative number.`);
          }
        }
        // If editedCost is empty, it's handled by the original_average_unit_cost fallback in `isValid` implicitly,
        // so we only check for actively invalid entries here.
      });
      
      if (!this.isValidOrderValue) {
        const orderValueMessage = this.getOrderValueMessage;
        if (this.getOrderValueStatus === 'error') { // Check if it's an actual error state
             errorMessages.push(`Order total issue: ${orderValueMessage}. Please adjust quantities or unit costs.`);
        }
      }

      if (errorMessages.length > 0) {
        EventBus.$emit("show-snackbar", { message: `Cannot submit PO: ${errorMessages.join(' ')}`, color: "error" });
        this.submitting = false;
        return;
      }
    }
    
    try {
      const poToSubmit = new PurchaseOrderModel({
        ...this.purchaseOrder,
        locationId: this.purchaseOrder.locationId,
        status: 'draft',
        items: this.selectedProducts.map(product => {
          const orderedQuantity = Number(product.quantity) || 0;
          const editedCost = product['[average_unit_cost]'];
          const originalCost = product.original_average_unit_cost || 0;
          let effectiveUnitPrice = originalCost;

          if (editedCost !== null && editedCost !== undefined && editedCost !== '') {
            const numEditedCost = Number(editedCost);
            if (!isNaN(numEditedCost) && numEditedCost >= 0) {
              effectiveUnitPrice = numEditedCost;
            }
          }
          return {
            variantId: product.variant_id,
            variantImage: product.variant_image,
            productName: product['Item[Item Description]'],
            variant: product.variant,
            sku: product.product_sku,
            supplierSku: product.supplier_sku || product.supplierSku || undefined, // Add supplier SKU
            orderedQuantity: orderedQuantity,
            receivedQuantity: 0,
            unitPrice: effectiveUnitPrice,
            totalPrice: orderedQuantity * effectiveUnitPrice
          };
        })
      });

      await poToSubmit.save();
      this.$emit('success', 'Purchase Order submitted successfully');
      this.$router.push(`/purchase-orders/${poToSubmit.id}`);
    } catch (error) {
      this.$emit('error', 'Failed to submit Purchase Order');
    } finally {
      this.submitting = false;
    }
  }

  // New Computed Properties for Forecasting - REMOVED (moved to InventoryForecasting.vue)
  /*
  get methodologyOptions(): Array<{ text: string, value: string, description: string }> {
    const baseOptions = [
      {
        text: 'Simple average moving',
        value: 'simple_average',
        description: 'Uses a straightforward average of historical data. Best for stable data.'
      },
      {
        text: 'Computing model',
        value: 'combined_weighted',
        description: 'Combines moving averages with yearly trends. Ideal for seasonal data.'
      }
    ];

    if (this.store && typeof this.store.isValidateRule === 'function' && this.store.isValidateRule(FEATURES.AI_ML_DEMAND_FORECASTING, 'unlocked')) {
      baseOptions.push({
        text: 'Smart model (using AI)',
        value: 'ai',
        description: 'AI-powered model for complex, dynamic data patterns. Date range not required for suggestion.'
      });
    }
    return baseOptions;
  }

  get currentMethodDescription(): string {
    const selectedMethod = this.methodologyOptions.find(m => m.value === this.selectedForecastingMethod);
    return selectedMethod ? selectedMethod.description : 'Select a forecasting method to see its description.';
  }

  get showDateFilter(): boolean {
    // Show date filter only if a method is selected and it's not the AI model
    return !!this.selectedForecastingMethod && 
           (this.selectedForecastingMethod === 'simple_average' || this.selectedForecastingMethod === 'combined_weighted');
  }
  */

  // Renamed to avoid conflict if child emits same name, or just reuse.
  private navigateToForecastSettings(): void {
    this.$router.push('/settings/inventory-forecast');
  }

  // Event handlers for InventoryForecasting component
  private handleDatesUpdated(newDates: string[]): void {
    this.dates = newDates;
    // The existing @Watch('dates') will trigger fetchData
  }

  private handleMethodApplied(): void {
    // Method saved by child, parent just needs to refetch data
    if (this.dataTableOptions.page !== 1) {
      this.dataTableOptions.page = 1; // Reset to first page if method changes
    } else {
      this.fetchData();
    }
  }

  private handleForecastError(message: string): void {
    this.$emit('error', message);
  }

  private handleForecastSuccess(message: string): void {
    this.$emit('success', message);
  }

  // + Add new method to handle emitted filter list
  private handleLocationFilterUpdate(filterList: FilterColumnList): void {
    this.activeLocationFilters = filterList;
    if (this.dataTableOptions.page !== 1) {
      this.dataTableOptions.page = 1;
    } else {
      this.fetchData();
    }
  }

  private openInteractiveGuide(): void {
    window.open('https://app.arcade.software/share/UtnYz7degDr51rbvpTJF', '_blank');
  }
}
